import { EditReservation } from 'types/reservations';
import { isEventAddOnAvailable } from 'types/shared/helper/eventAddOns';
import { Event, Ticket } from 'types/shared/documents/restaurants/event';

export type TicketSelection = NonNullable<EditReservation['ticket']>;
export type TicketCoupon = NonNullable<EditReservation['ticketCoupon']>;
export type TicketVoucher = NonNullable<EditReservation['ticketVoucher']>;
export type TicketValidTillUnit = NonNullable<
  EditReservation['ticketValidTillUnit']
>;

type TicketOption = NonNullable<Event['options']>[number];
type TicketSelectionField = 'options' | 'optionalAddOns';

export const getPaidAddOnMinimumGuests = (ticket?: Ticket | null) => {
  const paid = ticket?.paid || ticket?.payments?.some(payment =>
    !['voucher', 'coupon'].includes(String(payment.paidVia)) &&
    ['paid', 'authorized'].includes(String(payment.status)) &&
    Number(payment.amount) > 0
  );
  if (!paid) return 0;
  return Math.max(0, ...(ticket?.event?.optionalAddOns ?? [])
    .filter(addOn => (ticket?.optionalAddOns?.[addOn.id] ?? 0) > 0)
    .map(addOn => addOn.minGuests ?? 0));
};

const cleanCount = (value: unknown) =>
  Math.max(0, Math.floor(Number(value) || 0));

const failedTicketPaymentStatuses = ['canceled', 'failed', 'expired'];

const getPaymentNumber = (value: unknown) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : 0;

const isActiveTicketPayment = (
  payment: NonNullable<Ticket['payments']>[number]
) => !failedTicketPaymentStatuses.includes(String(payment.status));

export const getTicketPaymentAmount = (
  ticket: Ticket | null | undefined,
  paidVia: string
) =>
  (ticket?.payments ?? [])
    .filter((payment) => String(payment.paidVia) === paidVia)
    .filter(isActiveTicketPayment)
    .reduce((sum, payment) => sum + getPaymentNumber(payment.amount), 0);

export const getTicketPayableAmount = (ticket: Ticket | null | undefined) => {
  const ticketAmount = getPaymentNumber(ticket?.amount);
  const couponAmount = getTicketPaymentAmount(ticket, 'coupon');
  const voucherAmount = getTicketPaymentAmount(ticket, 'voucher');

  return Math.max(ticketAmount - couponAmount - voucherAmount, 0);
};

export const getTicketValidTillMinutes = (
  value = 2,
  unit: TicketValidTillUnit = 'hours'
) => {
  const normalizedValue = Math.max(1, Number(value) || 1);

  if (unit === 'days') return normalizedValue * 24 * 60;
  if (unit === 'hours') return normalizedValue * 60;

  return normalizedValue;
};

export const getTicketPartialPaymentFraction = (
  reservation: Pick<
    EditReservation,
    | 'ticketPartialPayment'
    | 'ticketPartialPaymentMode'
    | 'ticketPartialPaymentAmount'
  >,
  ticketTotal = 0
) => {
  if (reservation.ticketPartialPaymentMode === 'amount') {
    if (!ticketTotal) return 1;

    return (
      Math.min(
        Math.max(Number(reservation.ticketPartialPaymentAmount ?? 0), 0.01),
        ticketTotal
      ) / ticketTotal
    );
  }

  return Math.min(Math.max(reservation.ticketPartialPayment ?? 1, 0.01), 1);
};

const getCounter = (
  event: Event,
  field: TicketSelectionField,
  optionId: string
) => ((event as any).counter?.[field]?.[optionId] ?? 0) as number;

export const getOptionTitle = (option: TicketOption) =>
  option.translation?.de?.title ||
  option.translation?.en?.title ||
  option.internalName;

export const sortTicketOptions = (options: TicketOption[] = []) =>
  [...options].sort(
    (a, b) =>
      (a?.pos ?? 100) - (b?.pos ?? 100) ||
      a.internalName.localeCompare(b.internalName) ||
      (a.amount ?? 0) - (b.amount ?? 0)
  );

export const getRequiredTicketOptionCount = (event: Event, guests = 0) =>
  event.optionPer === 'perGroup' || event.per === 'perGroup'
    ? 1
    : cleanCount(guests);

export const getMinimumAddOnCount = (
  option: TicketOption,
  guests = 0
) => {
  if (!option.minAmount) return 0;
  return option.minAmount === 'GROUP_SIZE'
    ? cleanCount(guests)
    : cleanCount(option.minAmount);
};

export const getWholeGroupAddOnCount = (
  option: TicketOption,
  guests = 0
) => {
  if (!option.wholeGroupOnly) return null;

  return option.type === 'perGroup' ? 1 : cleanCount(guests);
};

export const getRemainingOptionCount = (
  event: Event,
  field: TicketSelectionField,
  option: TicketOption
) => {
  if (typeof option.limited !== 'number' || option.limited === 0)
    return Number.POSITIVE_INFINITY;

  return Math.max(0, option.limited - getCounter(event, field, option.id));
};

export const getDefaultTicketSelection = (
  event: Event,
  guests = 0
): TicketSelection => {
  const required = getRequiredTicketOptionCount(event, guests);
  const options: Record<string, number> = {};
  const optionalAddOns: Record<string, number> = {};

  for (const addOn of event.optionalAddOns ?? []) {
    if (!isEventAddOnAvailable(addOn, guests)) continue;
    const min = getMinimumAddOnCount(addOn, guests);
    const wholeGroupCount = getWholeGroupAddOnCount(addOn, guests);

    if (min) optionalAddOns[addOn.id] = wholeGroupCount ?? min;
  }

  if (!event.optional && required > 0) {
    let rest = required;

    for (const option of sortTicketOptions(event.options ?? [])) {
      if (!rest) break;

      const remaining = getRemainingOptionCount(event, 'options', option);
      if (remaining <= 0) continue;

      const next = Math.min(rest, remaining);
      options[option.id] = next;
      rest -= next;
    }
  }

  return { options, optionalAddOns };
};

export const normalizeTicketSelection = (
  event: Event,
  guests = 0,
  ticket?: EditReservation['ticket'],
  preservedAddOns: Record<string, number> = {}
): TicketSelection => {
  const defaults = getDefaultTicketSelection(event, guests);
  const required = getRequiredTicketOptionCount(event, guests);
  const options: Record<string, number> = {};
  const optionalAddOns: Record<string, number> = { ...defaults.optionalAddOns };

  for (const option of event.options ?? []) {
    const value = cleanCount(ticket?.options?.[option.id]);
    const max = getRemainingOptionCount(event, 'options', option);

    if (value) options[option.id] = Math.min(value, max);
  }

  for (const addOn of event.optionalAddOns ?? []) {
    if (!isEventAddOnAvailable(addOn, guests)) {
      if (preservedAddOns[addOn.id] > 0) optionalAddOns[addOn.id] = preservedAddOns[addOn.id];
      continue;
    }
    const min = getMinimumAddOnCount(addOn, guests);
    const value = cleanCount(ticket?.optionalAddOns?.[addOn.id]);
    const max = getRemainingOptionCount(event, 'optionalAddOns', addOn);
    const wholeGroupCount = getWholeGroupAddOnCount(addOn, guests);
    const next =
      wholeGroupCount !== null
        ? value > 0 || min > 0
          ? max >= wholeGroupCount
            ? wholeGroupCount
            : 0
          : 0
        : Math.min(Math.max(value, min), max);

    if (next) optionalAddOns[addOn.id] = next;
  }

  if (event.disallowMultipleOptions) {
    const selectedId = Object.entries(options).find(([, value]) => value > 0)?.[0];

    return {
      options: selectedId ? { [selectedId]: required } : defaults.options,
      optionalAddOns,
    };
  }

  let overflow =
    Object.values(options).reduce((sum, value) => sum + value, 0) - required;

  if (overflow > 0) {
    for (const [id, value] of Object.entries(options)) {
      if (!overflow) break;

      const remove = Math.min(value, overflow);
      options[id] -= remove;
      overflow -= remove;

      if (options[id] <= 0) delete options[id];
    }
  }

  return {
    options: Object.keys(options).length ? options : defaults.options,
    optionalAddOns,
  };
};

export const updateTicketSelection = ({
  event,
  guests = 0,
  ticket,
  field,
  id,
  next,
  preservedAddOns,
}: {
  event: Event;
  guests?: number;
  ticket?: EditReservation['ticket'];
  field: TicketSelectionField;
  id: string;
  next: number;
  preservedAddOns?: Record<string, number>;
}): TicketSelection => {
  const selection = normalizeTicketSelection(event, guests, ticket, preservedAddOns);
  const option =
    field === 'options'
      ? event.options?.find((o) => o.id === id)
      : event.optionalAddOns?.find((o) => o.id === id);

  if (!option) return selection;
  if (field === 'optionalAddOns' && !isEventAddOnAvailable(option, guests)) return selection;

  const values = { ...(selection[field] ?? {}) };
  const max = getRemainingOptionCount(event, field, option);
  const min = field === 'optionalAddOns' ? getMinimumAddOnCount(option, guests) : 0;
  const wholeGroupCount =
    field === 'optionalAddOns'
      ? getWholeGroupAddOnCount(option, guests)
      : null;
  const value =
    wholeGroupCount !== null
      ? cleanCount(next) > 0 || min > 0
        ? max >= wholeGroupCount
          ? wholeGroupCount
          : 0
        : 0
      : Math.min(Math.max(cleanCount(next), min), max);

  if (field === 'options') {
    const required = getRequiredTicketOptionCount(event, guests);

    if (event.disallowMultipleOptions) {
      return {
        ...selection,
        options: value > 0 ? { [id]: required } : {},
      };
    }

    if (value <= 0) {
      delete values[id];
    } else {
      values[id] = Math.min(value, required);
    }

    let overflow =
      Object.values(values).reduce((sum, current) => sum + current, 0) -
      required;

    if (overflow > 0) {
      for (const [optionId, current] of Object.entries(values)) {
        if (!overflow || optionId === id) continue;

        const remove = Math.min(current, overflow);
        values[optionId] -= remove;
        overflow -= remove;

        if (values[optionId] <= 0) delete values[optionId];
      }
    }

    return { ...selection, options: values };
  }

  if (value <= 0) {
    delete values[id];
  } else {
    values[id] = value;
  }

  return { ...selection, optionalAddOns: values };
};

export const getTicketSelectionError = (
  event: Event | null | undefined,
  guests = 0,
  ticket?: EditReservation['ticket'],
  preservedAddOns: Record<string, number> = {}
) => {
  if (!event || event.optional) return null;

  const selected = Object.values(ticket?.options ?? {}).reduce(
    (sum, value) => sum + cleanCount(value),
    0
  );
  const required = getRequiredTicketOptionCount(event, guests);

  if (selected !== required) {
    return required === 1
      ? 'Es muss genau eine Ticketoption ausgewählt werden.'
      : `Es müssen genau ${required} Ticketoptionen ausgewählt werden.`;
  }

  for (const addOn of event.optionalAddOns ?? []) {
    const min = getMinimumAddOnCount(addOn, guests);
    const selectedAddOns = cleanCount(ticket?.optionalAddOns?.[addOn.id]);
    if (!isEventAddOnAvailable(addOn, guests)) {
      if (selectedAddOns === preservedAddOns[addOn.id]) continue;
      if (selectedAddOns > 0) return `${getOptionTitle(addOn)} ist für diese Personenzahl nicht verfügbar.`;
      continue;
    }

    if (selectedAddOns < min) {
      return `${getOptionTitle(addOn)} muss mindestens ${min}x ausgewählt werden.`;
    }

    const wholeGroupCount = getWholeGroupAddOnCount(addOn, guests);

    if (
      wholeGroupCount !== null &&
      selectedAddOns > 0 &&
      selectedAddOns !== wholeGroupCount
    ) {
      return `${getOptionTitle(addOn)} muss für die gesamte Gruppe ausgewählt werden.`;
    }
  }

  return null;
};

const getCouponDiscount = (base: number, coupon?: TicketCoupon | null, guests = 0) => {
  if (!coupon?.discount?.value) return 0;

  if (coupon.discount.type === 'percentage') {
    const percentage =
      coupon.discount.value <= 1
        ? coupon.discount.value * 100
        : coupon.discount.value;

    return Number(((base * percentage) / 100).toFixed(2));
  }

  return coupon.per === 'perPerson'
    ? coupon.discount.value * Math.max(guests, 1)
    : coupon.discount.value;
};

const getVoucherAmount = (voucher?: TicketVoucher | null) => {
  if (!voucher?.valid) return 0;

  return Number(voucher.remainingAmount?.value ?? 0) || 0;
};

const getServiceFee = (
  event: Event,
  base: number,
  guests: number,
  enabled: boolean
) => {
  const tickets = (event as any).service_fee;

  if (!enabled || !tickets?.serviceFeeInPct || !guests) return 0;

  const gnautFee = Number(
    (tickets.gnautServiceFee?.type === 'fixed'
      ? tickets.gnautServiceFee?.value ?? 0
      : (tickets.gnautServiceFee?.value ?? 0.05) * (base / 100 / guests)
    ).toFixed(2)
  );
  const fee = Number((((tickets.serviceFeeInPct ?? 0) * gnautFee) / 100).toFixed(2));

  return (
    Math.max(
      Math.min(fee, tickets.maxServiceFee ?? fee),
      tickets.minServiceFee ?? 0
    ) * guests
  );
};

export const getTicketPaymentSummary = ({
  event,
  guests = 0,
  ticket,
  coupon,
  voucher,
  partialPayment = 1,
  includeServiceFee = true,
  preservedAddOns,
}: {
  event: Event;
  guests?: number;
  ticket?: EditReservation['ticket'];
  coupon?: TicketCoupon | null;
  voucher?: TicketVoucher | null;
  partialPayment?: number;
  includeServiceFee?: boolean;
  preservedAddOns?: Record<string, number>;
}) => {
  const selection = normalizeTicketSelection(event, guests, ticket, preservedAddOns);
  const ticketTotal =
    Object.entries(selection.options ?? {}).reduce((sum, [id, quantity]) => {
      const option = event.options?.find((o) => o.id === id);
      if (!option) return sum;

      return sum + ((event.amount ?? 0) + (option.amount ?? 0)) * quantity;
    }, 0) +
    Object.entries(selection.optionalAddOns ?? {}).reduce(
      (sum, [id, quantity]) => {
        const option = event.optionalAddOns?.find((o) => o.id === id);
        if (!option) return sum;

        return sum + (option.amount ?? 0) * quantity;
      },
      0
    );

  const normalizedPartialPayment = Math.min(Math.max(partialPayment || 1, 0.01), 1);
  const requiredOnlineBase = Number(
    (ticketTotal * normalizedPartialPayment).toFixed(2)
  );
  const discount = Math.min(
    getCouponDiscount(requiredOnlineBase, coupon, guests),
    requiredOnlineBase
  );
  const voucherAmount = Math.min(
    getVoucherAmount(voucher),
    Math.max(requiredOnlineBase - discount, 0)
  );
  const serviceFee = getServiceFee(
    event,
    requiredOnlineBase - discount - voucherAmount,
    guests,
    includeServiceFee
  );
  const guestPaysOnline = Math.max(
    requiredOnlineBase - discount - voucherAmount + serviceFee,
    0
  );
  const remainingAtRestaurant = Math.max(ticketTotal - requiredOnlineBase, 0);

  return {
    selection,
    ticketTotal,
    requiredOnlineBase,
    discount,
    voucherAmount,
    serviceFee,
    guestPaysOnline,
    remainingAtRestaurant,
    partialPayment: normalizedPartialPayment,
  };
};
